import {
    AfterContentInit,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IAction, Store, UnsubscribeCallback } from '@se/se.ui.advanced';
import { MediaService } from '@se/se.ui.html5';
import { Hotkey, HotkeysService } from 'angular2-hotkeys';
import { map, Subscription } from 'rxjs';
import { AppConfig } from '../../../app/app.config';
import { EcgConfiguration } from '../../../app/resolvers/ecg-configuration';
import { ArrangementType, CaliperMode, Format, GuidedComparisonType } from '../../models/emuns';
import { MessageActions, VISUAL_COMPARISON } from '../../redux/actions';
import { CustomCommandState, EcgState } from '../../redux/ecgstate';
import { EcgStore } from '../../redux/providers';
import { CultureMappingService } from '../../utils/culture.service';
import { ECG_CONSTANTS } from '../../utils/ecg-constants';
import { KeyValuePair } from '../../utils/key-value-pair';
import { PostMessage, PostMessageService, PostMessageType } from '../../utils/post-message.service';
import { ArrangementConfiguration } from '../../view-models/trace-viewmodel';

export interface SchillerFilters {
    lowFilter?: number | null;
    highFilter?: number | null;
}

@Component({
    selector: 'layout-fab-tools',
    templateUrl: './layout-fab-tools.component.html',
    styleUrls: ['./layout-fab-tools.component.scss'],
    encapsulation: ViewEncapsulation.None,
    viewProviders: [PostMessageService],
})
export class LayoutFabToolsComponent implements OnInit, OnChanges, AfterContentInit, OnDestroy {
    // Data Members

    isFabOpen: boolean = false;
    readonly globalInputValue = 'global';
    readonly leadInputValue = 'lead';

    private _isFullscreen: boolean = false;
    get isFullscreen() {
        return this._isFullscreen;
    }

    @Output()
    fullscreenChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Output() lowFilterChange: EventEmitter<number> = new EventEmitter<number>();

    private storeSubscriber: UnsubscribeCallback | null = null;
    private translateSubscriber: Subscription | null = null;

    @Input()
    chartLayersNumber: number = 0;

    @Input()
    arrangementConfiguration!: ArrangementConfiguration;

    @Input()
    set schillerFilters(filters: SchillerFilters) {
        this.lowFilter = filters?.lowFilter ?? 0;
        this.enableSchillerFilters = !!Object.keys(filters ?? {}).length;
    }

    @Input()
    ecgConfiguration!: EcgConfiguration;
    @Output()
    ecgConfigurationChange = new EventEmitter<EcgConfiguration>();

    get culture() {
        return this.store.getState().culture;
    }

    @Input()
    set culture(culture: number) {
        this.store.dispatch(MessageActions.cultureMessage(culture));
    }

    get paperOpacity() {
        return this.store.getState().chartState.paperOpacity;
    }

    @Input()
    set paperOpacity(paperOpacity: number) {
        this.store.dispatch(MessageActions.paperOpacityMessage(paperOpacity));
    }

    get traceSpacing() {
        return this.store.getState().chartState.traceSpacing;
    }

    @Input()
    set traceSpacing(traceSpacing: number) {
        this.store.dispatch(MessageActions.traceSpacingMessage(traceSpacing));
    }

    get isFitBaseline() {
        return this.store.getState().chartState.isFitBaseline;
    }

    @Input()
    set isFitBaseline(isFitBaseline: boolean) {
        this.store.dispatch(MessageActions.isFitBaselineMessage(isFitBaseline));
    }

    get studySpacing() {
        return this.store.getState().multiViewerState.studySpacing;
    }

    @Input()
    set studySpacing(studySpacing: number) {
        this.store.dispatch(MessageActions.studySpacingMessage(studySpacing));
    }

    get horizontalStudySpacing$() {
        return this.store.asObservable().pipe(
            map((state) => {
                return state?.multiViewerState.horizontalStudySpacing;
            }),
        );
    }

    get horizontalStudySpacing() {
        return this.store.getState().multiViewerState.horizontalStudySpacing;
    }

    @Input()
    set horizontalStudySpacing(horizontalStudySpacing: number) {
        this.store.dispatch(MessageActions.horizontalStudySpacingMessage(horizontalStudySpacing));
    }

    get zoomFactor() {
        return this.store.getState().chartState.zoomFactor;
    }

    @Input()
    set zoomFactor(zoomFactor: number) {
        this.store.dispatch(MessageActions.zoomFactorMessage(zoomFactor));
    }

    formats: Array<number> = [];

    get selectedFormat() {
        return this.store.getState().chartState.format;
    }

    @Input()
    set selectedFormat(selectedFormat: Format) {
        this.store.dispatch(MessageActions.formatMessage(selectedFormat));
    }

    @Input()
    enableStudiesSpacing = false;

    get summaryVisible() {
        return this.store.getState().detailsState.summaryState.visible;
    }

    @Input()
    set summaryVisible(summaryVisible: boolean) {
        this.store.dispatch(MessageActions.summaryVisibleMessage(summaryVisible));
    }

    get fiducialPointsVisible() {
        return this.store.getState().chartState.fiducialPointsVisible;
    }

    @Input()
    set fiducialPointsVisible(fiducialPointsVisible: boolean) {
        this.store.dispatch(MessageActions.fiducialPointsVisibleMessage(fiducialPointsVisible));
    }

    get printAsDisplayed() {
        return this.store.getState().chartState.printAsDisplayed;
    }

    @Input()
    set printAsDisplayed(value: boolean) {
        this.store.dispatch(MessageActions.printAsDisplayedMessage(value));
    }

    @Input() enabledPrintAsDisplayed: boolean = false;

    caliperModes: Array<number> = [];

    get caliperMode() {
        return this.store.getState().chartState.caliperMode;
    }

    @Input()
    set caliperMode(caliperMode: CaliperMode) {
        this.store.dispatch(MessageActions.caliperModeMessage(caliperMode));
    }

    arrangements: Array<KeyValuePair<string, number>> = [];
    private _selectedArrangementValue: ArrangementType | null = null;
    get selectedArrangementValue() {
        return this._selectedArrangementValue;
    }
    set selectedArrangementValue(arrangement: ArrangementType | null) {
        if (arrangement === null || this._selectedArrangementValue === arrangement) return;
        this._selectedArrangementValue = arrangement;

        const defaultGuidedComparison = ECG_CONSTANTS.defaultComparisonByArrangement[arrangement];

        this.selectedGuidedComparisonValue = defaultGuidedComparison;

        const arrangementObj = this.arrangements.find((item) => item.value === arrangement) ?? null;
        this._selectedArrangement = arrangementObj;
        this.store.dispatch(MessageActions.arrangementMessage(arrangement));
    }
    private _selectedArrangement: KeyValuePair<string, number> | null = null;
    get selectedArrangement() {
        return this._selectedArrangement;
    }
    @Input()
    set selectedArrangement(selectedArrangement) {
        if (!selectedArrangement) return;
        this.selectedArrangementValue = selectedArrangement.value;
    }

    timeAlignments: Array<number> = [];

    get selectedTimeAlignment() {
        return this.store.getState().chartState.timeAlignment;
    }

    @Input()
    set selectedTimeAlignment(selectedTimeAlignment) {
        this.store.dispatch(MessageActions.timeAligmentMessage(selectedTimeAlignment));
    }

    paperRates: Array<number> = [];

    get selectedPaperRate() {
        return this.store.getState().chartState.paperRate;
    }

    @Input()
    set selectedPaperRate(selectedPaperRate) {
        this.store.dispatch(MessageActions.paperRateMessage(selectedPaperRate));
    }

    get customCommandState() {
        return this.store.getState().toolbarState.customCommandState;
    }

    @Input()
    set customCommandState(customCommandState) {
        this.store.dispatch(MessageActions.customCommandMessage(customCommandState));
    }

    get detailsVisible() {
        return this.store.getState().detailsState.visible;
    }

    @Input()
    set detailsVisible(detailsVisible) {
        this.store.dispatch(MessageActions.detailsVisibleMessage(detailsVisible));
    }

    private _visualComparison = false;
    get visualComparison() {
        return this._visualComparison;
    }

    guidedComparisons: Array<KeyValuePair<string, number>> = [];

    private _selectedGuidedComparisonValue: GuidedComparisonType | null = null;
    get selectedGuidedComparisonValue() {
        return this._selectedGuidedComparisonValue;
    }
    set selectedGuidedComparisonValue(guidedComparison: GuidedComparisonType | null) {
        if (guidedComparison === null || this._selectedGuidedComparisonValue === guidedComparison) return;
        this._selectedGuidedComparisonValue = guidedComparison;
        if (this._selectedGuidedComparisonValue === GuidedComparisonType.SideBySide) {
            /**@TODO cambiare logica */
            const arrangementToSelect =
                this.arrangements.find((arr) => arr.value === ArrangementType.TwelveXOne) ?? null;
            this.selectedArrangement = arrangementToSelect;
        }
        const guidedComparisonObj = this.guidedComparisons.find((item) => item.value === guidedComparison) ?? null;
        this._selectedGuidedComparison = guidedComparisonObj;
        this.store.dispatch(
            MessageActions.guidedComparisonMessage({
                traceSpacing: ECG_CONSTANTS.GUIDED_COMPARISON_TRACE_SPACING[guidedComparison],
                studySpacing: ECG_CONSTANTS.GUIDED_COMPARISON_STUDIES_SPACING[guidedComparison],
                horizontalStudySpacing: ECG_CONSTANTS.GUIDED_COMPARISON_STUDIES_HORIZONTAL_SPACING[guidedComparison],
                guidedComparison: guidedComparison,
            }),
        );

        this.store.dispatch(MessageActions.fitMessage());
    }

    private _selectedGuidedComparison: KeyValuePair<string, number> | null = null;
    get selectedGuidedComparison() {
        return this._selectedGuidedComparison;
    }

    @Input()
    set selectedGuidedComparison(selectedGuidedComparisonObj) {
        if (!selectedGuidedComparisonObj) return;
        this.selectedGuidedComparisonValue = selectedGuidedComparisonObj.value;
    }

    @Input() set showCustomMeasuresButton(value: boolean) {
        if (value) {
            this.onCustomMeasuresChange(true);
        }
    }

    @Input() set showGlobalMeasures(value: boolean) {
        if (value && !this.selectedMeasures.includes(this.globalInputValue)) {
            this.selectedMeasures = [...this.selectedMeasures, this.globalInputValue];
        } else if (!value && this.selectedMeasures.includes(this.globalInputValue)) {
            let index = this.selectedMeasures.indexOf(this.globalInputValue);
            if (index !== -1) {
                this.selectedMeasures = this.selectedMeasures.filter((e) => e !== this.globalInputValue);
            }
        }
    }

    isShowingLeads = false;

    @Input() set showLeadsAnnotations(value: boolean) {
        this.isShowingLeads = value;
        if (value && !this.selectedMeasures.includes(this.leadInputValue)) {
            this.selectedMeasures = [...this.selectedMeasures, this.leadInputValue];
        } else if (!value && this.selectedMeasures.includes(this.leadInputValue)) {
            let index = this.selectedMeasures.indexOf(this.leadInputValue);
            if (index !== -1) {
                this.selectedMeasures = this.selectedMeasures.filter((e) => e !== this.leadInputValue);
            }
        }
    }

    // @Input() showLeadsAnnotations: boolean = false;
    @Input() showLeadsAnnotationsButton: boolean = false;
    @Input() showGlobalAnnotationsButton: boolean = false;

    @Output() customMeasuresChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() leadsAnnotationsChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    // @Output() globalAnnotationsChange: EventEmitter<any> = new EventEmitter<any>();

    // hotkeys

    private hkFullScreen: Hotkey | Hotkey[] | null = null;
    private hkArrangement: Hotkey | Hotkey[] | null = null;
    private hkFormat: Hotkey | Hotkey[] | null = null;
    private hkTime: Hotkey | Hotkey[] | null = null;
    private hkRate: Hotkey | Hotkey[] | null = null;
    private hkCaliperMode: Hotkey | Hotkey[] | null = null;
    private hkFiducialPoints: Hotkey | Hotkey[] | null = null;
    private hkOpacityUp: Hotkey | Hotkey[] | null = null;
    private hkOpacityDown: Hotkey | Hotkey[] | null = null;
    private hkTraceSpacingUp: Hotkey | Hotkey[] | null = null;
    private hkTraceSpacingDown: Hotkey | Hotkey[] | null = null;
    private hkStudySpacingUp: Hotkey | Hotkey[] | null = null;
    private hkStudySpacingDown: Hotkey | Hotkey[] | null = null;

    public enableSchillerFilters = false;

    public schillerFiltersConfiguration = {
        lowFilters: [0, 25, 40, 150],
    };
    public lowFilter: number = 0;

    public selectedMeasures: string[] = [];

    // Constructor

    constructor(
        public mediaService: MediaService,
        @Inject(EcgStore) private store: Store<EcgState>,
        private postMessageService: PostMessageService,
        private cultureMappingService: CultureMappingService,
        private translate: TranslateService,
        private appConfig: AppConfig,
        private hotkeysService: HotkeysService,
    ) {
        this.addHotKeys();
    }

    // public functions

    ngOnChanges(changes: SimpleChanges) {
        let change = changes['arrangementConfiguration'];
        if (change && change.currentValue !== change.previousValue) {
            this.updateSubToolsArray();
        }
    }

    ngAfterContentInit() {
        this.listenForEvents();
        this.updateSubToolsArray();
        this.applyEcgConfiguration();
    }

    ngOnDestroy() {
        this.storeSubscriber?.();
        this.translateSubscriber?.unsubscribe();

        this.removeHotKeys();
    }

    ngOnInit() {
        if (!!this.showCustomMeasuresButton) {
            setTimeout(() => this.onCustomMeasuresChange(true), 100);
        }
    }

    onCustomMeasuresChange(active: boolean) {
        this.customMeasuresChange.emit(active);
    }

    onLeadsAnnotationsChange(active: boolean) {
        this.leadsAnnotationsChange.emit(active);
    }

    // onGlobalAnnotationsChange() {
    //     this.globalAnnotationsChange.emit();
    // }

    onFit() {
        this.store.dispatch(MessageActions.fitMessage());
    }

    onFullscreenChange() {
        this._isFullscreen = !this._isFullscreen;
        this.fullscreenChange.emit(this.isFullscreen);
    }

    onSelectedArrangement() {
        let index = !!this.selectedArrangement ? this.arrangements.indexOf(this.selectedArrangement) : -1;
        index++;
        if (index > this.arrangements.length - 1) index = 0;
        if (index < 0) index = 0;

        this.selectedArrangement = this.arrangements[index];
    }

    onSelectedFormat() {
        this.selectedFormat = this.selectedFormat == 0 ? 1 : 0;
    }

    onSelectedMeasures() {
        const global = this.selectedMeasures.includes(this.globalInputValue);
        this.onCustomMeasuresChange(global);
        const lead = this.selectedMeasures.includes(this.leadInputValue);
        this.onLeadsAnnotationsChange(lead);
    }

    onPrintAsDisplayed() {
        this.printAsDisplayed = !this.printAsDisplayed;
    }

    onLowFilterChange(filter?: number) {
        if (!isNaN(filter)) {
            this.lowFilter = filter;
        } else {
            const currentIndex = this.schillerFiltersConfiguration.lowFilters.indexOf(this.lowFilter);
            const nextIndex = (currentIndex + 1) % this.schillerFiltersConfiguration.lowFilters.length;
            this.lowFilter = this.schillerFiltersConfiguration.lowFilters[nextIndex];
        }
        this.lowFilterChange.emit(this.lowFilter); //Nel caso gli mando la configurazione di tutti e due i filtri
    }

    onSelectedTimeAlignment() {
        this.selectedTimeAlignment = this.selectedTimeAlignment == 0 ? 1 : 0;
    }

    onSelectedPaperRate() {
        this.selectedPaperRate = this.selectedPaperRate == 0 ? 1 : 0;
    }

    onSelectedGuidedComparison() {
        let index = !!this.selectedGuidedComparison
            ? this.guidedComparisons.indexOf(this.selectedGuidedComparison)
            : -1;
        index++;
        if (index > this.guidedComparisons.length - 1) index = 0;
        if (index < 0) index = 0;

        this.selectedGuidedComparison = this.guidedComparisons[index];
    }

    onCaliperMode() {
        this.caliperMode = this.caliperMode == 0 ? 1 : 0;
    }

    onFiducialPoints() {
        this.fiducialPointsVisible = this.fiducialPointsVisible ? false : true;
    }

    onCustomCommand() {
        this.postMessageService.send(new PostMessage(this.store.getState(), PostMessageType.stateChanged));
    }

    isHorizontalSpacingEnabled = (): boolean =>
        this.visualComparison &&
        this.chartLayersNumber === 2 &&
        this.selectedArrangement?.value === ArrangementType.TwelveXOne &&
        this.selectedGuidedComparison?.value === GuidedComparisonType.SideBySide;

    // private functions

    private listenForEvents() {
        this.translateSubscriber = this.translate.onLangChange.subscribe(() => {
            let arrangementsIndex = !!this.selectedArrangement
                ? this.arrangements.indexOf(this.selectedArrangement)
                : -1;
            this.updateSubToolsArray();
            this.selectedArrangement = this.arrangements[arrangementsIndex];
        });

        this.storeSubscriber = this.store.subscribe((action: IAction) => {
            switch (action.type) {
                case VISUAL_COMPARISON:
                    this._visualComparison = action.payload;
            }
        });
    }

    private updateSubToolsArray() {
        this.arrangements = LayoutFabToolsComponent.initArrangementEnums(
            ArrangementType,
            this.translate,
            this.arrangementConfiguration,
        );
        if (this.arrangements && this.arrangements.length > 0) this.selectedArrangement = this.arrangements[0];
        this.guidedComparisons = LayoutFabToolsComponent.initEnums(GuidedComparisonType, this.translate);
        /*
        if (this.selectedArrangement?.value !== ArrangementType.TwelveXOne)
            this.guidedComparisons = this.guidedComparisons.filter(
                (comparison) => comparison.value !== GuidedComparisonType.Horizontal,
            ); */
        //this.guidedComparisons = guidedComparisons;

        if (this.guidedComparisons && this.guidedComparisons.length > 0)
            this.selectedGuidedComparison = this.guidedComparisons[0]; // default: TraceToTrace
    }

    private static initEnums(enumType: any, translate: TranslateService) {
        let array = new Array<KeyValuePair<string, number>>();

        let length = Object.keys(enumType).length;

        let keys = Object.keys(enumType).slice(length / 2, length);
        let values = Object.keys(enumType)
            .slice(0, length / 2)
            .map((key) => parseInt(key));

        for (let i = 0; i < length / 2; i++) array.push(new KeyValuePair(translate.instant(keys[i]), values[i]));

        return array;
    }

    private static initArrangementEnums(
        enumType: any,
        translate: TranslateService,
        arrangementConfiguration: ArrangementConfiguration,
    ) {
        if (arrangementConfiguration === null) return [];

        let array = LayoutFabToolsComponent.initEnums(enumType, translate);

        switch (arrangementConfiguration.leadCount) {
            case 15: // only XXOne, XXTwo and ThreeXFour
                array = array.slice(2, 3).concat(array.slice(6, 8));
                break;
            case 12: // remove XXOne, XXTwo
                array = array.slice(0, 6);

                // remove Median
                if (!arrangementConfiguration.derivedWaveformPresent) {
                    array = array.slice(0, array.length - 1);
                }
                break;
            default: // only XXOne, XXTwo
                array = array.slice(6, 8);
                break;
        }

        return array;
    }

    private addHotKeys() {
        this.hkFullScreen = this.hotkeysService.add(
            new Hotkey('ctrl+1', () => {
                this.onFullscreenChange();
                return true;
            }),
        );
        this.hkArrangement = this.hotkeysService.add(
            new Hotkey('ctrl+2', () => {
                this.onSelectedArrangement();
                return true;
            }),
        );
        this.hkFormat = this.hotkeysService.add(
            new Hotkey('ctrl+3', () => {
                this.onSelectedFormat();
                return true;
            }),
        );
        this.hkTime = this.hotkeysService.add(
            new Hotkey('ctrl+4', () => {
                this.onSelectedTimeAlignment();
                return true;
            }),
        );
        this.hkRate = this.hotkeysService.add(
            new Hotkey('ctrl+5', () => {
                this.onSelectedPaperRate();
                return true;
            }),
        );
        this.hkCaliperMode = this.hotkeysService.add(
            new Hotkey('ctrl+6', () => {
                this.onCaliperMode();
                return true;
            }),
        );
        this.hkFiducialPoints = this.hotkeysService.add(
            new Hotkey('ctrl+7', () => {
                this.onFiducialPoints();
                return true;
            }),
        );
        this.hkOpacityUp = this.hotkeysService.add(
            new Hotkey('alt+o+up', () => {
                if (this.paperOpacity < 1) this.paperOpacity += 0.1;
                else this.paperOpacity = 1;
                return true;
            }),
        );
        this.hkOpacityDown = this.hotkeysService.add(
            new Hotkey('alt+o+down', () => {
                if (this.paperOpacity > 0) this.paperOpacity -= 0.1;
                else this.paperOpacity = 0;
                return true;
            }),
        );
        this.hkTraceSpacingUp = this.hotkeysService.add(
            new Hotkey('alt+t+up', () => {
                if (this.traceSpacing < 2) this.traceSpacing += 0.005;
                else this.traceSpacing = 2;
                return true;
            }),
        );
        this.hkTraceSpacingDown = this.hotkeysService.add(
            new Hotkey('alt+t+down', () => {
                if (this.traceSpacing > 0) this.traceSpacing -= 0.005;
                else this.traceSpacing = 0;
                return true;
            }),
        );
        this.hkStudySpacingUp = this.hotkeysService.add(
            new Hotkey('alt+s+up', () => {
                if (this.studySpacing < 2) this.studySpacing += 0.005;
                else this.studySpacing = 2;
                return true;
            }),
        );
        this.hkStudySpacingDown = this.hotkeysService.add(
            new Hotkey('alt+s+down', () => {
                if (this.studySpacing > 0) this.studySpacing -= 0.005;
                else this.studySpacing = 0;
                return true;
            }),
        );
    }

    private removeHotKeys() {
        this.hotkeysService.remove(this.hkFullScreen ?? undefined);
        this.hotkeysService.remove(this.hkArrangement ?? undefined);
        this.hotkeysService.remove(this.hkFormat ?? undefined);
        this.hotkeysService.remove(this.hkTime ?? undefined);
        this.hotkeysService.remove(this.hkRate ?? undefined);
        this.hotkeysService.remove(this.hkCaliperMode ?? undefined);
        this.hotkeysService.remove(this.hkFiducialPoints ?? undefined);
        this.hotkeysService.remove(this.hkOpacityUp ?? undefined);
        this.hotkeysService.remove(this.hkOpacityDown ?? undefined);
        this.hotkeysService.remove(this.hkTraceSpacingUp ?? undefined);
        this.hotkeysService.remove(this.hkTraceSpacingDown ?? undefined);
        this.hotkeysService.remove(this.hkStudySpacingUp ?? undefined);
        this.hotkeysService.remove(this.hkStudySpacingDown ?? undefined);
    }

    private async applyEcgConfiguration() {
        if (!this.appConfig.isLoaded) await this.appConfig.load('./assets/config.json');

        let cultureName = this.appConfig.getValue('culture');
        this.culture =
            this.ecgConfiguration != null && this.ecgConfiguration.culture.read
                ? this.ecgConfiguration.culture.value
                : this.cultureMappingService.getCultureTypeByCultureName(cultureName);

        if (this.ecgConfiguration == null) return;

        if (this.ecgConfiguration.detailsvisible.read) this.detailsVisible = this.ecgConfiguration.detailsvisible.value;

        if (this.ecgConfiguration.layoutConfiguration != null) {
            if (this.ecgConfiguration.layoutConfiguration.arrangement.read)
                this.selectedArrangement =
                    this.arrangements.find(
                        (arrangement) =>
                            arrangement.value == this.ecgConfiguration.layoutConfiguration.arrangement.value,
                    ) ?? null;
            if (this.ecgConfiguration.layoutConfiguration.paperRate.read)
                this.selectedPaperRate = this.ecgConfiguration.layoutConfiguration.paperRate.value;
            if (this.ecgConfiguration.layoutConfiguration.paperOpacity.read)
                this.paperOpacity = this.ecgConfiguration.layoutConfiguration.paperOpacity.value;
        }

        if (this.ecgConfiguration.customCommandConfiguration != null) {
            let customCommandState = new CustomCommandState();

            if (this.ecgConfiguration.customCommandConfiguration.visible.read)
                customCommandState.visible = this.ecgConfiguration.customCommandConfiguration.visible.value;
            if (this.ecgConfiguration.customCommandConfiguration.text.read)
                customCommandState.text = this.ecgConfiguration.customCommandConfiguration.text.value;
            if (this.ecgConfiguration.customCommandConfiguration.source.read)
                customCommandState.source = this.ecgConfiguration.customCommandConfiguration.source.value;

            this.customCommandState = customCommandState;
        }
    }
}
